export const REGISTRY_VERSION = 'woundos-product-evidence-registry-v1';
export const FIELD_LABELS = { identity:'Identity', generic_class:'Class / construction', materials:'Materials',
  active_ingredients:'Ingredient statements', indications:'Stated uses', wound_types:'Wound types',
  preparation:'Preparation', application:'Application / removal statements', change_instructions:'Time / change statements',
  contraindications:'Restriction statements', warnings:'Precaution statements', compatibility:'Compatibility statements',
  sizing:'Size / shape statements', secondary_layers:'Secondary layer / fixation', billing:'Billing claims — not validated', notes:'Complete source notes' };
const object = v => v && typeof v === 'object' && !Array.isArray(v);
const array = (v,max) => Array.isArray(v) && v.length <= max;
const id = v => typeof v === 'string' && /^[a-z]_[0-9a-f]{24}$/.test(v);
const norm = v => v.replace(/[™®]/g,'').toLowerCase().replace(/\s+/g,' ').trim();
const utf8 = v => new TextEncoder().encode(v);
async function sha(v) {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',utf8(v)))).map(b=>b.toString(16).padStart(2,'0')).join('');
}
export async function validateRegistry(value) {
  if (!object(value) || value.schema_version !== REGISTRY_VERSION || value.scope !== 'private_source_review_not_clinical_advice'
    || !array(value.sources,32) || !array(value.records,20000) || !array(value.claims,200000)
    || !array(value.products,20000) || !array(value.nonproduct_spans,50000)
    || value.summary?.clinical_use_authorized !== false) throw new Error('Invalid product registry.');
  const sources = new Map(), records = new Map(), claims = new Map();
  let total=0;
  for (const s of value.sources) {
    if (!id(s.id) || sources.has(s.id) || typeof s.text !== 'string' || typeof s.filename !== 'string'
      || s.original_verified !== false || s.currency_verified !== false) throw new Error('Invalid source identity.');
    total += s.text.length;
    if (total > 64000000 || await sha(s.text) !== s.sha256 || utf8(s.text).length !== s.bytes
      || s.id !== `s_${s.sha256.slice(0,24)}`) throw new Error('Source checksum mismatch.');
    sources.set(s.id,s);
  }
  // Python offsets count Unicode code points, not UTF-16 code units.
  const chars = new Map(Array.from(sources,([k,v])=>[k,Array.from(v.text)]));
  const interval = (v,s) => Number.isInteger(v.start) && Number.isInteger(v.end) && v.start>=0 && v.end>=v.start && v.end<=chars.get(s.id).length;
  const quote = ref => chars.get(ref.source_id)?.slice(ref.start,ref.end).join('');
  for (const r of value.records) {
    const s=sources.get(r.source_id);
    if (!s || !id(r.id) || records.has(r.id) || !interval(r,s) || typeof r.name!=='string'
      || r.runtime_authorized!==false || !array(r.raw_fields,100) || !array(r.flags,100)) throw new Error('Invalid product source record.');
    for (const f of r.raw_fields) {
      if (!object(f.source) || f.source.source_id!==r.source_id || f.source.start<r.start || f.source.end>r.end
        || quote(f.source)!==f.source.quote) throw new Error('Changed source field.');
    }
    records.set(r.id,r);
  }
  for (const c of value.claims) {
    const r=records.get(c.record_id),ref=c.source;
    if (!r || r.status!=='named_source_record' || !id(c.id) || claims.has(c.id) || !Object.hasOwn(FIELD_LABELS,c.field)
      || !object(ref) || ref.source_id!==r.source_id || ref.start<r.start || ref.end>r.end || quote(ref)!==ref.quote
      || c.verification!=='source_statement_not_independently_verified') throw new Error('Unbound product statement.');
    const exact = c.field==='identity' ? r.name_source : r.raw_fields.find(f=>f.source.start===ref.start && f.source.end===ref.end)?.source;
    if (!exact || exact.quote!==ref.quote) throw new Error('Statement not in the retained record.');
    claims.set(c.id,c);
  }
  const productIds=new Set();
  for (const p of value.products) {
    if (!id(p.id) || productIds.has(p.id) || typeof p.name!=='string' || !array(p.record_ids,1000)
      || !array(p.source_groups,32) || !array(p.flags,100) || p.clinical_use_authorized!==false
      || p.complete_IFU_verified!==false || !object(p.fields) || Object.keys(p.fields).length!==Object.keys(FIELD_LABELS).length) throw new Error('Invalid product projection.');
    productIds.add(p.id);
    if (!p.record_ids.length || p.record_ids.some(k=>records.get(k)?.name!==p.name)) throw new Error('Wrong product ownership.');
    for (const field of Object.keys(FIELD_LABELS)) {
      const f=p.fields[field];
      if (!f || !array(f.claim_ids,10000) || f.claim_ids.some(k=>claims.get(k)?.field!==field || !p.record_ids.includes(claims.get(k)?.record_id))
        || f.status !== (f.claim_ids.length ? 'source_statements_available' : 'not_stated_in_supplied_product_record')) throw new Error('Changed field ownership or gap.');
    }
  }
  for (const [key,s] of sources) {
    const parts=[...value.records.filter(r=>r.source_id===key),...value.nonproduct_spans.filter(r=>r.source_id===key)].sort((a,b)=>a.start-b.start);
    let at=0;
    for (const r of parts) { if(r.start!==at || !interval(r,s))throw new Error('Source partition mismatch.');at=r.end; }
    if (at!==chars.get(key).length) throw new Error('Incomplete source retention.');
  }
  return { data:value, sources, records, claims, chars };
}
export function findProducts(registry, query, group='') {
  const terms=norm(query).split(' ').filter(Boolean);
  return registry.data.products.filter(p=>!group || p.source_groups.includes(group)).filter(p=>{
    const text=norm(p.name+' '+p.record_ids.map(k=>registry.records.get(k).raw_fields.map(f=>f.source.quote).join(' ')).join(' '));
    return terms.every(t=>text.includes(t));
  }).sort((a,b)=>Number(norm(b.name)===norm(query))-Number(norm(a.name)===norm(query)) || a.name.localeCompare(b.name));
}
export function fieldStatements(registry, product, field) {
  const result = new Map();
  for (const key of product.fields[field].claim_ids) {
    const c=registry.claims.get(key),s=registry.sources.get(c.source.source_id);
    if (!result.has(c.source.quote)) result.set(c.source.quote,{text:c.source.quote,method:c.assignment,locations:[]});
    result.get(c.source.quote).locations.push(`${s.filename}: lines ${c.source.line_start}–${c.source.line_end}`);
  }
  return [...result.values()];
}
